import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { authService, normalizeUserType } from '@/api/services/authService'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate } from 'react-router-dom'
import { getApiError } from '@/api/client'
import type { UserType } from '@/shared/types'

export interface LoginPayload {
  email: string
  password: string
  user_type: UserType
}

export function useLogin() {
  const setAuth = useAuthStore(s => s.setAuth)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (payload: LoginPayload) =>
      authService.login({
        email: payload.email,
        password: payload.password,
        user_type: payload.user_type.toUpperCase(),
      }),
    onSuccess: ({ user, token }) => {
      setAuth(user, token)
      navigate(user.user_type === 'admin' ? '/dashboard' : '/school/overview', { replace: true })
    },
    onError: (err) => {
      console.error('[Login Error]', getApiError(err))
    },
  })
}

// Refreshes the persisted user (roles, school, photo…) from who-am-i once the
// session is authenticated. A failed call keeps the persisted user as-is.
export function useSyncMe() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const setAuth = useAuthStore(s => s.setAuth)

  useEffect(() => {
    if (!isAuthenticated) return
    // Only the admin who-am-i is wired; collectors/assistants have their own profile flow.
    const current = normalizeUserType(useAuthStore.getState().user?.user_type)
    if (current !== 'admin' && current !== 'school_admin') return
    let cancelled = false
    void authService.me().then(user => {
      if (cancelled || !user) return
      const { token: storedToken, user: stored } = useAuthStore.getState()
      // Merge, don't replace: who-am-i may omit fields the login returned (school, roles, photo).
      // user_type is already normalised (SYSTEM_ADMIN → 'admin'), which also heals a stale
      // 'system_admin' left in storage by an earlier build.
      const defined = Object.fromEntries(
        Object.entries(user).filter(([, v]) => v !== undefined && v !== null),
      ) as Partial<typeof user>
      setAuth({ ...(stored ?? user), ...defined, user_type: current === 'school_admin' ? current : user.user_type },
        storedToken ?? user.token)
    })
    return () => { cancelled = true }
  }, [isAuthenticated, setAuth])
}

export function useLogout() {
  const logout = useAuthStore(s => s.logout)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      logout()
      navigate('/login', { replace: true })
    },
  })
}
